// Feature-based permission helpers for backend-provided granular permissions
import React from 'react';
import { usePermissionStore } from '../permissions';
import { AccessMap, UserGroup } from '../../types/access';

/**
 * Hook to access permission helpers
 * These helpers work with backend-provided granular permissions
 * No group-based logic - only permission flags from backend
 */
export const usePermissionHelpers = () => {
  const store = usePermissionStore();
  
  // Create legacy compatibility methods
  const legacyMethods = {
    // Legacy canView method - maps to new permission system
    canView: (feature: keyof AccessMap) => {
      if (!store.permissions || store.noGroups) return false;
      
      // Map legacy feature names to new module names
      const moduleMap: Record<keyof AccessMap, string> = {
        dashboard: 'dashboard',
        lane: 'lanes',
        queue: 'queues',
        calendar: 'calendar',
        checkpoint: 'admin_checkpoints',
        terminal: 'admin_terminals',
        system: 'admin',
        admin: 'admin',
        powerBI: 'power_bi',
        visualization2D: 'visualization_2d',
        visualization3D: 'visualization_3d'
      };
      
      const moduleName = moduleMap[feature];
      if (moduleName === 'admin') {
        return store.hasAdminAccess();
      }
      if (moduleName === 'power_bi') {
        return store.hasPermission('admin_bi_dashboard_access');
      }
      return store.canViewModule(moduleName);
    },
    
    // Legacy canUpdate method - maps to new permission system
    canUpdate: (feature: keyof AccessMap) => {
      if (!store.permissions || store.noGroups) return false;
      
      const moduleMap: Record<keyof AccessMap, string> = {
        dashboard: 'dashboard',
        lane: 'lanes',
        queue: 'queues',
        calendar: 'calendar',
        checkpoint: 'admin_checkpoints',
        terminal: 'admin_terminals',
        system: 'admin',
        admin: 'admin',
        powerBI: 'power_bi',
        visualization2D: 'visualization_2d',
        visualization3D: 'visualization_3d'
      };
      
      const moduleName = moduleMap[feature];
      if (moduleName === 'admin') {
        return store.hasAdminAccess();
      }
      if (moduleName === 'power_bi') {
        return false; // PowerBI doesn't have update access
      }
      return store.canUpdateModule(moduleName);
    },
    
    // Legacy hasAnyAccess method
    hasAnyAccess: (feature: keyof AccessMap) => {
      return legacyMethods.canView(feature) || legacyMethods.canUpdate(feature);
    },
    
    // Legacy getAccessLevel method
    getAccessLevel: (feature: keyof AccessMap) => {
      if (legacyMethods.canUpdate(feature)) return 'update';
      if (legacyMethods.canView(feature)) return 'view';
      return 'none';
    }
  };
  
  return {
    // Core permission checks (legacy compatibility)
    canView: legacyMethods.canView,
    canUpdate: legacyMethods.canUpdate,
    hasAnyAccess: legacyMethods.hasAnyAccess,
    getAccessLevel: legacyMethods.getAccessLevel,
    getUserGroup: store.getUserGroup,
    
    // State information
    permissions: store.permissions,
    loading: store.loading,
    error: store.error,
    noGroups: store.noGroups,
    environment: store.environment,
    loadTimestamp: store.loadTimestamp,
    currentUser: store.currentUser,
    userGroups: store.userGroups,
    
    // Actions
    setPermissions: store.setPermissions,
    setUserGroups: store.setUserGroups,
    setCurrentUser: store.setCurrentUser,
    setLoading: store.setLoading,
    setError: store.setError,
    setNoGroups: store.setNoGroups,
    setEnvironmentInfo: store.setEnvironmentInfo,
    clearError: store.clearError,
    reset: store.reset,
    
    // Permission checks (from permissionChecks object)
    canViewAdminMenu: permissionChecks.canViewAdminMenu,
    canAccessPowerBI: permissionChecks.canAccessPowerBI,
    canViewCalendar: permissionChecks.canViewCalendar,
    canUpdateCalendar: permissionChecks.canUpdateCalendar
  };
};

/**
 * Legacy compatibility helper - maps old AccessMap features to new permissions
 */
const mapLegacyFeatureToNewPermission = (feature: keyof AccessMap, action: 'view' | 'update' | 'any') => {
  const store = usePermissionStore.getState();
  if (!store.permissions || store.noGroups) return false;
  
  const moduleMap: Record<keyof AccessMap, string> = {
    dashboard: 'dashboard',
    lane: 'lanes',
    queue: 'queues',
    calendar: 'calendar',
    checkpoint: 'admin_checkpoints',
    terminal: 'admin_terminals',
    system: 'admin',
    admin: 'admin',
    powerBI: 'power_bi',
    visualization2D: 'visualization_2d',
    visualization3D: 'visualization_3d'
  };
  
  const moduleName = moduleMap[feature];
  
  if (moduleName === 'admin') {
    return store.hasAdminAccess();
  }
  
  if (moduleName === 'power_bi') {
    if (action === 'update') return false;
    return store.hasPermission('admin_bi_dashboard_access');
  }
  
  switch (action) {
    case 'view':
      return store.canViewModule(moduleName);
    case 'update':
      return store.canUpdateModule(moduleName);
    case 'any':
      return store.canAccessModule(moduleName);
    default:
      return false;
  }
};

/**
 * Check if user can view a specific feature
 * @param feature - The feature to check
 * @returns boolean - True if user can view the feature
 */
export const canView = (feature: keyof AccessMap): boolean => {
  return mapLegacyFeatureToNewPermission(feature, 'view');
};

/**
 * Check if user can update a specific feature
 * @param feature - The feature to check
 * @returns boolean - True if user can update the feature
 */
export const canUpdate = (feature: keyof AccessMap): boolean => {
  return mapLegacyFeatureToNewPermission(feature, 'update');
};

/**
 * Check if user can enforce calendar schedules
 * @returns boolean - True if user can enforce calendar schedules
 */
export const canEnforceCalendar = (): boolean => {
  return mapLegacyFeatureToNewPermission('calendar', 'any');
};

/**
 * Check if user has any access to a specific feature
 * @param feature - The feature to check
 * @returns boolean - True if user has any access to the feature
 */
export const hasAnyAccess = (feature: keyof AccessMap): boolean => {
  return mapLegacyFeatureToNewPermission(feature, 'any');
};

/**
 * Get the access level for a specific feature
 * @param feature - The feature to check
 * @returns AccessLevel - The access level for the feature
 */
export const getAccessLevel = (feature: keyof AccessMap): 'none' | 'view' | 'update' => {
  if (mapLegacyFeatureToNewPermission(feature, 'update')) return 'update';
  if (mapLegacyFeatureToNewPermission(feature, 'view')) return 'view';
  return 'none';
};

/**
 * Get the user's group information
 * @returns UserGroup | null - The user's group information or null if no groups
 */
export const getUserGroup = (): UserGroup | null => {
  const store = usePermissionStore.getState();
  return store.getUserGroup();
};

/**
 * Check if permissions are loaded
 * @returns boolean - True if permissions are loaded
 */
export const isPermissionsLoaded = (): boolean => {
  const store = usePermissionStore.getState();
  return store.permissions !== null;
};

/**
 * Check if permissions are loading
 * @returns boolean - True if permissions are loading
 */
export const isPermissionsLoading = (): boolean => {
  const store = usePermissionStore.getState();
  return store.loading;
};

/**
 * Check if there's an error with permissions
 * @returns boolean - True if there's an error
 */
export const hasPermissionError = (): boolean => {
  const store = usePermissionStore.getState();
  return store.error !== null;
};

/**
 * Get permission error message
 * @returns string | null - The error message
 */
export const getPermissionError = (): string | null => {
  const store = usePermissionStore.getState();
  return store.error;
};

/**
 * Check if user has no assigned groups
 * @returns boolean - True if user has no assigned groups
 */
export const hasNoGroups = (): boolean => {
  const store = usePermissionStore.getState();
  return store.noGroups;
};

/**
 * Get appropriate access message based on user's permission status
 * @returns string - The access message to display
 */
export const getAccessMessage = (): string => {
  const store = usePermissionStore.getState();
  
  if (store.noGroups) {
    return 'You are not assigned to any user groups. Please contact your administrator to get access.';
  }
  
  if (store.error) {
    return `Access Error: ${store.error}`;
  }
  
  if (store.loading) {
    return 'Loading permissions...';
  }
  
  return 'Access Restricted';
};



/**
 * Feature-specific permission checks
 */
export const permissionChecks = {
  // Store state properties for debugging
  get currentUser() {
    const store = usePermissionStore.getState();
    return store.currentUser;
  },
  
  get userGroups() {
    const store = usePermissionStore.getState();
    return store.userGroups;
  },

  // Checkpoint permissions
  canViewCheckpoints: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_checkpoint_view_access'] === true;
  },
  canUpdateCheckpoints: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_checkpoint_update_access'] === true;
  },
  
  // Lane permissions
  canViewLanes: () => canView('lane'),
  canUpdateLanes: () => canUpdate('lane'),
  canViewBeltPage: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // Implication: If lane_status_update is true, user should have view access even if lane_view is false
    return store.permissions['lane_view'] === true || store.permissions['lane_status_update'] === true;
  },
  canUpdateLaneStatus: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['lane_status_update'] === true;
  },
  
  // Terminal permissions
  canViewTerminals: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_terminal_view_access'] === true;
  },
  canUpdateTerminals: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    if (!store.permissions['admin_terminal_view_access']) {
      return false;
    }
    return store.permissions['admin_terminal_update_access'] === true;
  },

  // Queue Type permissions
  canViewQueueTypes: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_queuetype_view_access'] === true;
  },

  canUpdateQueueTypes: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_queuetype_update_access'] === true;
  },

  // Queue Assignment permissions
  canViewQueueAssignments: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_queueassignment_view_access'] === true;
  },

  canUpdateQueueAssignments: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_queueassignment_update_access'] === true;
  },

  // Section permissions
  canViewSections: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_section_view_access'] === true;
  },

  canUpdateSections: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_section_update_access'] === true;
  },

  // Asset permissions
  canViewAssets: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_asset_view_access'] === true;
  },

  canUpdateAssets: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    if (!store.permissions['admin_asset_view_access']) {
      return false;
    }
    return store.permissions['admin_asset_update_access'] === true;
  },

  // Checkpoint-specific permissions
  canToggleCheckpointStatus: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['dashboard_checkpoint_status_update'] === true;
  },

  canManageCheckpointExceptions: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // For now, use checkpoint update access as a proxy for exception management
    return store.permissions['admin_checkpoint_update_access'] === true;
  },

  canEditCheckpointButton: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_checkpoint_update_access'] === true;
  },

  // Lane-specific permissions
  canModifyLaneAssignments: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['queue_assignment_update'] === true;
  },

  canEditLaneButton: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['lane_status_update'] === true;
  },



  // Dashboard-specific permissions
  canViewAnalytics: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // For now, use dashboard view access as a proxy for analytics
    return store.permissions['dashboard_terminal-tile_view'] === true || 
           store.permissions['dashboard_checkpoint-tile_view'] === true ||
           store.permissions['dashboard_queue-tile_view'] === true ||
           store.permissions['dashboard_lane-tile_view'] === true;
  },

  canViewLidarData: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // For now, use dashboard view access as a proxy for lidar data
    return store.permissions['dashboard_terminal-tile_view'] === true || 
           store.permissions['dashboard_checkpoint-tile_view'] === true ||
           store.permissions['dashboard_queue-tile_view'] === true ||
           store.permissions['dashboard_lane-tile_view'] === true;
  },

  // Calendar-specific permissions
  canCreateCalendarEvents: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['calendar_update_access'] === true;
  },

  canDeleteCalendarEvents: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['calendar_update_access'] === true;
  },

  canEnforceCalendar: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // For now, use calendar update access as a proxy for enforce functionality
    return store.permissions['calendar_update_access'] === true;
  },

  canEditCalendarButton: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['calendar_update_access'] === true;
  },

  canCreateCalendarButton: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['calendar_update_access'] === true;
  },

  canDeleteCalendarButton: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['calendar_update_access'] === true;
  },

  canEnforceCalendarButton: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // For now, use calendar update access as a proxy for enforce functionality
    return store.permissions['calendar_update_access'] === true;
  },

  // System permissions
  canEditSystemConfig: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // For now, use admin access as a proxy for system config editing
    return store.permissions['admin_checkpoint_update_access'] === true || 
           store.permissions['admin_terminal_update_access'] === true ||
           store.permissions['calendar_update_access'] === true;
  },

  canManageUsers: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // For now, use admin access as a proxy for user management
    return store.permissions['admin_checkpoint_update_access'] === true || 
           store.permissions['admin_terminal_update_access'] === true ||
           store.permissions['calendar_update_access'] === true;
  },



  // Asset Group permissions
  canViewAssetGroups: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_assetgroup_view_access'] === true;
  },

  canUpdateAssetGroups: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    if (!store.permissions['admin_assetgroup_view_access']) {
      return false;
    }
    return store.permissions['admin_assetgroup_update_access'] === true;
  },
  
  // Dashboard permissions
  canViewDashboard: () => canView('dashboard'),
  
  // Dashboard tile permissions
  canViewTerminalTile: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['dashboard_terminal-tile_view'] === true;
  },
  canViewCheckpointTile: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['dashboard_checkpoint_status_update'] === true;
  },
  canViewQueueTile: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['dashboard_queue-tile_view'] === true;
  },
  canViewLaneTile: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['dashboard_lane-tile_view'] === true;
  },
  canUpdateCheckpointStatus: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['dashboard_checkpoint_status_update'] === true;
  },
  
  // Calendar permissions
  canViewCalendar: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['calendar_view_access'] === true;
  },
  canUpdateCalendar: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    if (!store.permissions['calendar_view_access']) {
      return false;
    }
    return store.permissions['calendar_update_access'] === true;
  },
  
  // PowerBI permissions
  canAccessPowerBI: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['admin_bi_dashboard_access'] === true;
  },
  
  // Queue permissions
  canViewQueuePage: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    // Implication: If queue_status_update OR queue_assignment_update is true, user should have view access even if queue_view is false
    return store.permissions['queue_view'] === true || 
           store.permissions['queue_status_update'] === true || 
           store.permissions['queue_assignment_update'] === true;
  },
  canUpdateQueueStatus: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['queue_status_update'] === true;
  },
  canUpdateQueueAssignment: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['queue_assignment_update'] === true;
  },
  
  // Queue permissions (legacy)
  canViewQueue: () => canView('queue'),
  canUpdateQueue: () => canUpdate('queue'),
  
  // Admin menu visibility - based on actual admin permissions (independent of group names)
  canViewAdminMenu: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    
    // Check if user has ANY admin permissions (view or update access to admin features)
    // This is completely independent of group names - only checks actual permissions
    // NOTE: admin_bi_dashboard_access is now in 'reporting' category, not admin
    const hasAdminAccess = store.permissions['calendar_view_access'] === true ||
                          store.permissions['admin_terminal_view_access'] === true ||
                          store.permissions['admin_checkpoint_view_access'] === true ||
                          store.permissions['admin_queuetype_view_access'] === true ||
                          store.permissions['admin_queueassignment_view_access'] === true ||
                          store.permissions['admin_section_view_access'] === true ||
                          store.permissions['admin_asset_view_access'] === true ||
                          store.permissions['admin_assetgroup_view_access'] === true ||
                          store.permissions['admin_display_view_access'] === true;
    
    // Also check for admin update permissions
    const hasAdminUpdateAccess = store.permissions['calendar_update_access'] === true ||
                                store.permissions['admin_terminal_update_access'] === true ||
                                store.permissions['admin_checkpoint_update_access'] === true ||
                                store.permissions['admin_queuetype_update_access'] === true ||
                                store.permissions['admin_queueassignment_update_access'] === true ||
                                store.permissions['admin_section_update_access'] === true ||
                                store.permissions['admin_asset_update_access'] === true ||
                                store.permissions['admin_assetgroup_update_access'] === true ||
                                store.permissions['admin_display_update_access'] === true;
    
    const finalAccess = hasAdminAccess || hasAdminUpdateAccess;
    
    console.log('canViewAdminMenu check:', {
      hasAdminAccess,
      hasAdminUpdateAccess,
      finalAccess,
      permissions: store.permissions,
      note: 'Admin access based on permissions, not group names'
    });
    
    return finalAccess;
  },

  // Group-based user role information
  getUserGroup: () => {
    const store = usePermissionStore.getState();
    return store.getUserGroup();
  },

  // Get user's display name from group (completely independent of hardcoded names)
  getUserDisplayName: () => {
    const store = usePermissionStore.getState();
    const userGroup = store.getUserGroup();
    if (!userGroup) {
      // getUserDisplayName: No user group found, showing fallback
      return 'User Group Not Found';
    }
    
    // Use the actual displayName from configuration, not hardcoded values
    return userGroup.displayName;
  },

  // Get user's group name
  getUserGroupName: () => {
    const store = usePermissionStore.getState();
    const userGroup = store.getUserGroup();
    if (!userGroup) {
      // getUserGroupName: No user group found, showing fallback
      return 'Group Not Found';
    }
    return userGroup.groupName;
  },

  // Check if user belongs to a specific group
  isUserInGroup: (groupName: string) => {
    const store = usePermissionStore.getState();
    if (!store.userGroups || store.noGroups) return false;
    return store.userGroups.some((group: UserGroup) => 
      group.groupName.toLowerCase() === groupName.toLowerCase()
    );
  },

  // Check if user has any admin permissions (independent of group names)
  hasAnyAdminPermissions: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    
    // Check for any admin view or update permissions
    // This is completely independent of group names - only checks actual permissions
    const adminPermissions = [
      'admin_bi_dashboard_access',
      'calendar_view_access', 'calendar_update_access',
      'admin_terminal_view_access', 'admin_terminal_update_access',
      'admin_checkpoint_view_access', 'admin_checkpoint_update_access',
      'admin_queuetype_view_access', 'admin_queuetype_update_access',
      'admin_queueassignment_view_access', 'admin_queueassignment_update_access',
      'admin_section_view_access', 'admin_section_update_access',
      'admin_asset_view_access', 'admin_asset_update_access',
      'admin_assetgroup_view_access', 'admin_assetgroup_update_access',
      'admin_display_view_access', 'admin_display_update_access'
    ];
    
    const hasAdminAccess = adminPermissions.some(permission => store.permissions![permission] === true);
    
    // hasAnyAdminPermissions check
    
    return hasAdminAccess;
  },

  // Visualization permissions - NEW
  can2dVisualisationAccess: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['2d_visualisation_access'] === true;
  },

  can3dVisualisationAccess: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['3d_visualisation_access'] === true;
  },

  // Combined visualization access check
  canAccessVisualization: () => {
    const store = usePermissionStore.getState();
    if (!store.permissions || store.noGroups) return false;
    return store.permissions['2d_visualisation_access'] === true || 
           store.permissions['3d_visualisation_access'] === true;
  },


};

/**
 * Permission guard component props
 */
export interface PermissionGuardProps {
  feature: keyof AccessMap;
  action?: 'view' | 'update' | 'any';
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Permission guard component
 * Only renders children if user has the required permission
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({ 
  feature, 
  action = 'view', 
  fallback = null, 
  children 
}) => {
  let hasPermission = false;
  
  switch (action) {
    case 'view':
      hasPermission = canView(feature);
      break;
    case 'update':
      hasPermission = canUpdate(feature);
      break;
    case 'any':
      hasPermission = hasAnyAccess(feature);
      break;
  }
  
  return hasPermission ? React.createElement(React.Fragment, null, children) : React.createElement(React.Fragment, null, fallback);
};

/**
 * Group-based guard component
 * Only renders children if user belongs to specified group(s)
 */
export const GroupGuard: React.FC<{ 
  groupNames: string[];
  fallback?: React.ReactNode; 
  children: React.ReactNode; 
}> = ({ groupNames, fallback = null, children }) => {
  const store = usePermissionStore.getState();
  const userGroup = store.getUserGroup();
  
  if (!userGroup) return React.createElement(React.Fragment, null, fallback);
  
  const hasAccess = groupNames.some(groupName => 
    userGroup.groupName.toLowerCase() === groupName.toLowerCase()
  );
  
  return hasAccess ? React.createElement(React.Fragment, null, children) : React.createElement(React.Fragment, null, fallback);
}; 